import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  Alert,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress
} from '@mui/material';
import { PlayArrow, Stop, EventNote, Receipt, MyLocation } from '@mui/icons-material';
import { formatTimeEntry } from '../utils/dateUtils';
import { timeService } from '../services/timeService';
import { useAuth } from '../hooks/useAuth';
import { getCurrentPosition, getDeviceName, LOCATION_PERMISSION_DENIED, LOCATION_ENABLE_INSTRUCTIONS } from '../hooks/usePunchLocation';

const WF_OTHER = '__other__';
const formatTime = (v) => formatTimeEntry(v, 'MM/dd/yyyy HH:mm');

/**
 * Data Collection (Kiosk) — when not logged in: only Clock In and Clock Out with PIN.
 * When logged in: also View Timecard and View Paystub.
 */
const TimeTrackingKiosk = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isLoggedIn = !!user;
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pinAction, setPinAction] = useState(null);
  const [pin, setPin] = useState('');
  const [punching, setPunching] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [timecardDialogOpen, setTimecardDialogOpen] = useState(false);
  const [timecardData, setTimecardData] = useState(null);
  const [timecardLoading, setTimecardLoading] = useState(false);
  const [requestingLocation, setRequestingLocation] = useState(false);
  const [kioskWorkOrders, setKioskWorkOrders] = useState([]);
  const [kioskWorkFunctions, setKioskWorkFunctions] = useState([]);
  const [kioskMetaLoading, setKioskMetaLoading] = useState(false);
  const [clockWorkOrderId, setClockWorkOrderId] = useState('');
  const [clockWorkFunctionId, setClockWorkFunctionId] = useState('');
  const [clockFunctionOther, setClockFunctionOther] = useState('');
  // Two-step state (kept for compatibility with earlier UI variants)
  const [clockInStep, setClockInStep] = useState('pin');
  const [clockInChecking, setClockInChecking] = useState(false);
  const [clockOutStep, setClockOutStep] = useState('pin');
  const [clockOutChecking, setClockOutChecking] = useState(false);
  const [openPunchInfo, setOpenPunchInfo] = useState(null);
  const [kioskGate, setKioskGate] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    axios
      .get('/api/public/kiosk-enabled')
      .then((res) => {
        if (!cancelled) setKioskGate(res.data?.enabled !== false ? 'ok' : 'blocked');
      })
      .catch(() => {
        if (!cancelled) setKioskGate('ok');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAllowLocation = async () => {
    setRequestingLocation(true);
    setError('');
    try {
      await getCurrentPosition();
      setError('');
    } catch (err) {
      const msg = err.message === LOCATION_PERMISSION_DENIED
        ? `${err.message} ${LOCATION_ENABLE_INSTRUCTIONS}`
        : (err.message || 'Location is required.');
      setError(msg);
    } finally {
      setRequestingLocation(false);
    }
  };

  const openPinDialog = (action) => {
    setPinAction(action);
    setPin('');
    setError('');
    setSuccessMessage('');
    if (action === 'clock_in' || action === 'clock_out') {
      setClockWorkOrderId('');
      setClockWorkFunctionId('');
      setClockFunctionOther('');
    }
    if (action === 'clock_in') {
      setClockInStep('pin');
    }
    if (action === 'clock_out') {
      setClockOutStep('pin');
      setOpenPunchInfo(null);
    }
    setPinDialogOpen(true);
  };

  const closePinDialogFull = () => {
    closePinDialog();
    setClockInStep('pin');
    setClockOutStep('pin');
    setOpenPunchInfo(null);
  };

  useEffect(() => {
    const needsMeta = pinAction === 'clock_out';
    const pinTrim = String(pin).trim();
    if (!pinDialogOpen || !needsMeta) {
      setKioskWorkOrders([]);
      setKioskWorkFunctions([]);
      return undefined;
    }
    if (!pinTrim) {
      setKioskWorkOrders([]);
      setKioskWorkFunctions([]);
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      setKioskMetaLoading(true);
      (async () => {
        try {
          const [woRes, wfRes] = await Promise.all([
            timeService.kioskOpenWorkOrders(pinTrim),
            timeService.kioskWorkFunctions(pinTrim)
          ]);
          if (!cancelled) {
            setKioskWorkOrders(woRes?.data ?? []);
            setKioskWorkFunctions(wfRes?.data ?? []);
          }
        } catch (e) {
          if (!cancelled) {
            setError(e.response?.data?.message || e.message || 'Failed to load work orders / functions');
          }
        } finally {
          if (!cancelled) setKioskMetaLoading(false);
        }
      })();
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [pinDialogOpen, pinAction, pin]);

  const closePinDialog = () => {
    setPinDialogOpen(false);
    setPinAction(null);
    setPin('');
    setError('');
    setSuccessMessage('');
  };

  const handlePinSubmit = async () => {
    const pinTrim = String(pin).trim();
    if (!pinTrim) {
      setError('Enter your PIN');
      return;
    }

    if (pinAction === 'clock_in') {
      // One-step clock-in: PIN only.
      // Prevent double clock-in: if there's already an open punch, block.
      try {
        await timeService.kioskOpenPunch(pinTrim);
        setError('You are already clocked in. Please clock out first.');
        return;
      } catch (err) {
        const status = err.response?.status;
        const isNotClockedIn = status === 400 && err.response?.data?.error === 'Not clocked in';
        if (!isNotClockedIn) {
          setError(err.response?.data?.message || err.message || 'Failed to check punch status');
          return;
        }
      }

      try {
        setPunching(true);
        setError('');
        let position = null;
        try {
          position = await getCurrentPosition();
        } catch (locErr) {
          if (locErr.message === LOCATION_PERMISSION_DENIED) {
            setError(`${locErr.message} Clocking in without location. ${LOCATION_ENABLE_INSTRUCTIONS}`);
          }
        }
        const device_name = getDeviceName();
        if (!device_name || device_name === 'unknown') {
          setError('Device name is required. Set it in settings or use a named device.');
          setPunching(false);
          return;
        }
        const res = await timeService.kioskPunch({
          pin: pinTrim,
          action: 'clock_in',
          latitude_in: position ? position.latitude : null,
          longitude_in: position ? position.longitude : null,
          device_name,
          device_info: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        });
        setError('');
        setSuccessMessage(`Clocked in as ${res.employeeName || 'OK'}`);
        setTimeout(closePinDialogFull, 1500);
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Failed to clock in');
      } finally {
        setPunching(false);
      }
      return;
    }

    if (pinAction === 'clock_out') {
      try {
        setPunching(true);
        setError('');
        let position = null;
        try {
          position = await getCurrentPosition();
        } catch (locErr) {
          if (locErr.message === LOCATION_PERMISSION_DENIED) {
            setError(`${locErr.message} Clocking out without location. ${LOCATION_ENABLE_INSTRUCTIONS}`);
          }
        }
        const device_name = getDeviceName();
        if (!device_name || device_name === 'unknown') {
          setError('Device name is required.');
          setPunching(false);
          return;
        }

        // For safety/backwards-compat: if WO/function are not selected in the UI,
        // attempt to prefill them from the open punch (if present).
        let openPunch = null;
        try {
          const openRes = await timeService.kioskOpenPunch(pinTrim);
          openPunch = openRes?.data ?? null;
        } catch (_) {
          // Ignore: backend will validate open punch existence and fail if needed.
        }

        const work_order_id =
          clockWorkOrderId && String(clockWorkOrderId).trim() !== ''
            ? clockWorkOrderId
            : openPunch?.work_order_id ?? undefined;

        if (clockWorkFunctionId === WF_OTHER && !String(clockFunctionOther || '').trim()) {
          setError('Enter a description when Function is Other.');
          return;
        }

        const workFunctionId =
          clockWorkFunctionId === WF_OTHER
            ? ''
            : clockWorkFunctionId && String(clockWorkFunctionId).trim() !== ''
              ? clockWorkFunctionId
              : openPunch?.work_function_id ?? undefined;

        const workFunctionOther =
          clockWorkFunctionId === WF_OTHER
            ? String(clockFunctionOther || '').trim()
            : clockWorkFunctionId && String(clockWorkFunctionId).trim() !== ''
              ? undefined
              : openPunch?.work_function_other ?? undefined;

        const res = await timeService.kioskPunch({
          pin: pinTrim,
          action: 'clock_out',
          latitude_out: position ? position.latitude : null,
          longitude_out: position ? position.longitude : null,
          device_name,
          device_info: typeof navigator !== 'undefined' ? navigator.userAgent : '',
          work_order_id,
          workFunctionId,
          workFunctionOther
        });
        setError('');
        setSuccessMessage(`Clocked out. ${res.employeeName || 'Done'}`);
        setTimeout(closePinDialogFull, 1500);
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Failed to clock out');
      } finally {
        setPunching(false);
      }
      return;
    }

    if (pinAction === 'timecard') {
      if (!isLoggedIn) {
        setError('Please log in to the app to view your timecard.');
        return;
      }
      try {
        setTimecardLoading(true);
        setError('');
        const res = await timeService.kioskTimecard(pinTrim);
        setTimecardData(res);
        closePinDialogFull();
        setTimecardDialogOpen(true);
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Failed to load timecard');
      } finally {
        setTimecardLoading(false);
      }
      return;
    }

    if (pinAction === 'paystub') {
      setError('Please log in to the app to view paystub.');
      return;
    }
  };

  if (kioskGate === 'loading') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (kioskGate === 'blocked') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 3,
          px: 2,
          bgcolor: 'background.default',
        }}
      >
        <Alert severity="info" sx={{ maxWidth: 420, mb: 2 }}>
          Kiosk mode has been turned off. Sign in on the normal login page to use the full app, or ask an administrator to turn kiosk back on.
        </Alert>
        <Button variant="contained" onClick={() => navigate('/login', { replace: true })}>
          Go to sign in
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 3,
        px: 2,
        bgcolor: 'background.default',
      }}
    >
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Data Collection
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Tap an option, then enter your PIN
      </Typography>

      <Button
        variant="outlined"
        size="small"
        startIcon={<MyLocation />}
        onClick={handleAllowLocation}
        disabled={requestingLocation}
        sx={{ mb: 2 }}
        title="Click first to trigger Chrome's location permission popup"
      >
        {requestingLocation ? 'Requesting…' : 'Allow Location (required for clock in/out)'}
      </Button>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 360, width: '100%' }}>
        <Button
          variant="contained"
          color="success"
          size="large"
          startIcon={<PlayArrow />}
          onClick={() => openPinDialog('clock_in')}
          sx={{ py: 2, fontSize: '1.1rem', fontWeight: 600 }}
        >
          Clock In
        </Button>
        <Button
          variant="contained"
          color="error"
          size="large"
          startIcon={<Stop />}
          onClick={() => openPinDialog('clock_out')}
          sx={{ py: 2, fontSize: '1.1rem', fontWeight: 600 }}
        >
          Clock Out
        </Button>
        {isLoggedIn && (
          <>
            <Button
              variant="outlined"
              size="large"
              startIcon={<EventNote />}
              onClick={() => openPinDialog('timecard')}
              sx={{ py: 2, fontSize: '1rem' }}
            >
              View Timecard
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<Receipt />}
              onClick={() => openPinDialog('paystub')}
              sx={{ py: 2, fontSize: '1rem' }}
            >
              View Paystub
            </Button>
          </>
        )}
      </Box>

      <Link
        component="button"
        variant="body2"
        onClick={() => navigate('/login')}
        sx={{ mt: 3, cursor: 'pointer' }}
      >
        Log in to full app
      </Link>

      {/* PIN dialog */}
      <Dialog open={pinDialogOpen} onClose={closePinDialogFull} maxWidth="xs" fullWidth>
        <DialogTitle>
          {pinAction === 'clock_in' && 'Clock In'}
          {pinAction === 'clock_out' && 'Clock Out'}

          {pinAction === 'timecard' && 'View Timecard'}
          {pinAction === 'paystub' && 'View Paystub'}
        </DialogTitle>
        <DialogContent>
          {successMessage && (
            <Alert severity="success" sx={{ mb: 1 }}>{successMessage}</Alert>
          )}
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 1 }}
              onClose={() => setError('')}
              action={
                error.includes('Location') && (
                  <Button
                    color="inherit"
                    size="small"
                    startIcon={<MyLocation />}
                    onClick={handleAllowLocation}
                    disabled={requestingLocation}
                  >
                    {requestingLocation ? 'Requesting…' : 'Allow Location'}
                  </Button>
                )
              }
            >
              {error}
            </Alert>
          )}

          {/* Clock-in (single modal): PIN only */}
          {pinAction === 'clock_in' && (
            <>
          {/* Clock-in is PIN only now */}
              <TextField
                fullWidth label="PIN" type="password"
                inputProps={{ inputMode: 'numeric', maxLength: 10, autoComplete: 'off' }}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => e.key === 'Enter' && handlePinSubmit()}
                autoFocus disabled={punching}
                sx={{ mt: 1 }}
              />
            </>
          )}

      {/* Clock-out (single modal): work order + function dropdowns + PIN */}
          {pinAction === 'clock_out' && (
        <>
          {kioskMetaLoading && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Loading jobs & functions…
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 1.5 }}>
            <FormControl fullWidth size="small" disabled={kioskMetaLoading || punching}>
              <InputLabel>Job (open work order)</InputLabel>
              <Select
                label="Job (open work order)"
                value={clockWorkOrderId}
                onChange={(e) => setClockWorkOrderId(e.target.value)}
              >
                <MenuItem value="">—</MenuItem>
                {kioskWorkOrders.map((wo) => (
                  <MenuItem key={wo.id} value={String(wo.id)}>
                    #{wo.work_order_identifier || wo.work_order_number}{wo.customer_name ? ` — ${wo.customer_name}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" disabled={kioskMetaLoading || punching}>
              <InputLabel>Function</InputLabel>
              <Select
                label="Function"
                value={clockWorkFunctionId}
                onChange={(e) => {
                  setClockWorkFunctionId(e.target.value);
                  if (e.target.value !== WF_OTHER) setClockFunctionOther('');
                }}
              >
                <MenuItem value="">—</MenuItem>
                {kioskWorkFunctions.map((wf) => (
                  <MenuItem key={wf.id} value={String(wf.id)}>{wf.label}</MenuItem>
                ))}
                <MenuItem value={WF_OTHER}>Other…</MenuItem>
              </Select>
            </FormControl>

            {clockWorkFunctionId === WF_OTHER && (
              <TextField
                fullWidth size="small"
                label="Function (describe)"
                value={clockFunctionOther}
                onChange={(e) => setClockFunctionOther(e.target.value)}
                placeholder="One-off task"
                disabled={punching}
              />
            )}
          </Box>

          <TextField
            fullWidth label="PIN" type="password"
            inputProps={{ inputMode: 'numeric', maxLength: 10, autoComplete: 'off' }}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\\D/g, ''))}
            onKeyDown={(e) => e.key === 'Enter' && handlePinSubmit()}
            autoFocus disabled={punching}
            sx={{ mt: 1 }}
          />
        </>
          )}

          {/* Other actions: timecard / paystub */}
          {(pinAction === 'timecard' || pinAction === 'paystub') && (
            <TextField
              fullWidth label="PIN" type="password"
              inputProps={{ inputMode: 'numeric', maxLength: 10, autoComplete: 'off' }}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => e.key === 'Enter' && handlePinSubmit()}
              autoFocus disabled={timecardLoading}
              sx={{ mt: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePinDialogFull} disabled={punching}>Cancel</Button>
          {pinAction === 'clock_in' && (
            <Button variant="contained" color="success" onClick={handlePinSubmit} disabled={punching}>
              {punching ? <CircularProgress size={24} /> : 'Clock In'}
            </Button>
          )}
          {pinAction === 'clock_out' && (
            <Button variant="contained" color="error" onClick={handlePinSubmit} disabled={punching}>
              {punching ? <CircularProgress size={24} /> : 'Clock Out'}
            </Button>
          )}
          {(pinAction === 'timecard' || pinAction === 'paystub') && (
            <Button variant="contained" onClick={handlePinSubmit} disabled={punching || timecardLoading}>
              {punching || timecardLoading ? <CircularProgress size={24} /> : 'OK'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Timecard modal */}
      <Dialog open={timecardDialogOpen} onClose={() => setTimecardDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Timecard — {timecardData?.employeeName || 'Last 7 days'}</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Clock In</TableCell>
                  <TableCell>Clock Out</TableCell>
                  <TableCell>Hours</TableCell>
                  <TableCell>Job</TableCell>
                  <TableCell>Function</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(timecardData?.data || []).map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{formatTime(row.clock_in)}</TableCell>
                    <TableCell>{formatTime(row.clock_out)}</TableCell>
                    <TableCell>{row.hours_worked != null ? Number(row.hours_worked).toFixed(2) : '—'}</TableCell>
                    <TableCell>{row.work_order_identifier || row.work_order_number || '—'}</TableCell>
                    <TableCell>{row.function_label || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {(!timecardData?.data || timecardData.data.length === 0) && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No time entries in the last 7 days.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTimecardDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TimeTrackingKiosk;
